import { ModuleIntro, ModuleBanner } from "@/components/ModuleIntro";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { LanguageToggle } from "@/components/LanguageToggle";
import { IdrSlider } from "@/components/idr/IdrSlider";
import { IdrResults } from "@/components/idr/IdrResults";
import { IdrEntourage } from "@/components/idr/IdrEntourage";
import IdrSeanceModule from "@/components/idr/IdrSeanceModule";
import { IDR_ITEMS, IDR_SCALE, IDR_T } from "@/data/idr";
import { usePatientMode } from "@/lib/patientMode";

const PER_PAGE = 10;
const TOTAL_PAGES = Math.ceil(IDR_ITEMS.length / PER_PAGE);
const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

const TopBar = ({ lang, setLang, t }) => {
  const { patientMode } = usePatientMode();
  return (
    <div className="flex items-center justify-between mb-6 no-print">
      {patientMode ? <span /> : <Link to="/" data-testid="idr-home-link" className="text-sm text-[var(--ink-soft)] hover:text-[var(--gold)] transition-colors">{t.allModules}</Link>}
      <LanguageToggle lang={lang} onChange={setLang} />
    </div>
  );
};

const Welcome = ({ t, lang, setLang, onStart }) => (
  <div className="animate-fade-up">
    <TopBar lang={lang} setLang={setLang} t={t} />
    <ModuleBanner moduleKey="idr-50" />
    <h1 className="font-serif-title text-4xl sm:text-5xl lg:text-6xl text-[var(--gold)] leading-tight mb-6 text-center">{t.welcomeTitle}</h1>
    <p className="text-[var(--ink)] leading-relaxed max-w-xl mx-auto mb-8 text-center">{t.welcomeText}</p>
    <ModuleIntro moduleKey="idr-50" lang={lang} className="mb-8" />
    <IdrEntourage variant="invite" lang={lang} />
    <div className="text-center">
      <button onClick={onStart} data-testid="idr-btn-start" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">{t.start}</button>
    </div>
  </div>
);

const Consigne = ({ t, lang, setLang, scale, onPrev, onNext }) => (
  <div className="animate-fade-up">
    <TopBar lang={lang} setLang={setLang} t={t} />
    <h2 className="font-serif-title text-3xl sm:text-4xl text-[var(--gold)] mb-6 text-center">{t.consigne}</h2>
    <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-6 text-[var(--ink)] leading-relaxed space-y-3">
      <p>{t.consigneP1}</p>
      <p>{t.scaleIntro}</p>
      <div className="flex items-center justify-between text-xs text-[var(--ink-soft)] px-1">
        <span data-testid="idr-scale-left">{t.scaleLeft}</span>
        <span data-testid="idr-scale-right">{t.scaleRight}</span>
      </div>
      <div className="grid grid-cols-5 gap-2">
        {scale.map((s) => (
          <div key={s.value} className="rounded-xl bg-white/70 border border-[var(--border)] p-3 text-center">
            <div className="text-xl font-serif-title text-[var(--gold)]">{s.value} %</div>
            <div className="text-[0.68rem] leading-tight text-[var(--ink-soft)] mt-1">{s.label}</div>
          </div>
        ))}
      </div>
      <p className="text-sm text-[var(--ink-soft)]">{t.resultsLater}</p>
    </div>
    <div className="flex items-center justify-between mt-8">
      <button onClick={onPrev} className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">{t.prev}</button>
      <button onClick={onNext} data-testid="idr-btn-begin" className="px-8 py-3 rounded-full bg-[var(--gold)] text-white font-medium hover:brightness-105 shadow-sm">{t.begin}</button>
    </div>
  </div>
);

const ItemsPage = ({ t, lang, setLang, scale, page, answers, onAnswer, onPrev, onNext }) => {
  const items = IDR_ITEMS.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);
  const answeredCount = Object.keys(answers).filter((k) => Number.isFinite(answers[k])).length;
  const pct = Math.round((answeredCount / IDR_ITEMS.length) * 100);
  const pageComplete = items.every((it) => Number.isFinite(answers[it.n]));

  return (
    <div className="animate-fade-up">
      <TopBar lang={lang} setLang={setLang} t={t} />
      <div className="mb-8" data-testid="idr-progress">
        <div className="flex items-baseline justify-between mb-2">
          <span className="text-sm uppercase tracking-[0.18em] text-[var(--ink-soft)]">{t.step} {page + 1} {t.of} {TOTAL_PAGES}</span>
          <span className="font-serif-title text-2xl text-[var(--gold)]" data-testid="idr-progress-pct">{pct} %</span>
        </div>
        <div className="h-3 w-full rounded-full bg-[var(--beige)] overflow-hidden">
          <div className="h-full rounded-full bg-[var(--gold)] transition-all duration-500 ease-out" style={{ width: `${pct}%` }} />
        </div>
      </div>

      <div className="space-y-4">
        {items.map((it) => (
          <IdrSlider key={it.n} id={`idr-${it.n}`} statement={it[lang]} index={it.n} scale={scale} value={answers[it.n]} onChange={(v) => onAnswer(it.n, v)} anchorLeft={t.scaleLeft} anchorRight={t.scaleRight} hint={t.sliderHint} />
        ))}
      </div>

      <div className="flex items-center justify-between mt-8 no-print">
        <button onClick={onPrev} data-testid="idr-btn-prev" className="px-6 py-3 rounded-full border border-[var(--gold)] text-[var(--gold)] font-medium hover:bg-[var(--gold)] hover:text-white transition-colors">{t.prev}</button>
        <button onClick={onNext} disabled={!pageComplete} data-testid="idr-btn-next"
          className={`px-8 py-3 rounded-full font-medium transition-all ${pageComplete ? "bg-[var(--gold)] text-white hover:brightness-105 shadow-sm" : "bg-[var(--beige)] text-[var(--ink-soft)] cursor-not-allowed"}`}>
          {page === TOTAL_PAGES - 1 ? t.seeResults : t.next}
        </button>
      </div>
      {!pageComplete && <p className="text-center text-xs text-[var(--ink-soft)] mt-3 no-print">{t.completeStep}</p>}
    </div>
  );
};

export default function IdrModule({ initialLang }) {
  const seance = new URLSearchParams(window.location.search).get("seance");
  if (seance) return <IdrSeanceModule token={seance} />;
  return <IdrClassic initialLang={initialLang} />;
}

function IdrClassic({ initialLang }) {
  const [lang, setLang] = useState(initialLang || "fr");
  const [phase, setPhase] = useState("welcome");
  const [page, setPage] = useState(0);
  const [answers, setAnswers] = useState({});
  const t = IDR_T[lang];
  const scale = IDR_SCALE[lang];

  const onAnswer = (n, v) => setAnswers((prev) => ({ ...prev, [n]: v }));
  const nextPage = () => { if (page < TOTAL_PAGES - 1) setPage((p) => p + 1); else setPhase("results"); scrollTop(); };
  const prevPage = () => { if (page > 0) setPage((p) => p - 1); else setPhase("consigne"); scrollTop(); };
  const restart = () => { setAnswers({}); setPage(0); setPhase("welcome"); scrollTop(); };

  const render = () => {
    if (phase === "welcome") return <Welcome t={t} lang={lang} setLang={setLang} onStart={() => { setPhase("consigne"); scrollTop(); }} />;
    if (phase === "consigne") return <Consigne t={t} lang={lang} setLang={setLang} scale={scale} onPrev={() => { setPhase("welcome"); scrollTop(); }} onNext={() => { setPhase("items"); setPage(0); scrollTop(); }} />;
    if (phase === "items") return <ItemsPage t={t} lang={lang} setLang={setLang} scale={scale} page={page} answers={answers} onAnswer={onAnswer} onPrev={prevPage} onNext={nextPage} />;
    return <IdrResults answers={answers} lang={lang} onRestart={restart} />;
  };

  return <Layout>{render()}</Layout>;
}
